import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Chip, Link, Tooltip, Typography } from '@mui/material';
import CodeBlock from './CodeBlock';
import CopyButton from './CopyButton';

const MAX_CHIPS = 6;
const MAX_ROWS = 4;

const monoSx = { fontFamily: 'monospace', fontSize: '0.85rem' };

const clampSx = (lines) => ({
  overflowWrap: 'anywhere',
  display: '-webkit-box',
  WebkitLineClamp: String(lines),
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

// Values are stored as text; JSON is recognised by its brackets so a plain word is never parsed.
const parseValue = (raw) => {
  const text = raw?.trim() ?? '';
  if (text === 'true' || text === 'false') return text === 'true';
  if (text !== '' && !isNaN(Number(text))) return Number(text);
  if (/^[[{]/.test(text)) {
    try {
      return JSON.parse(text);
    } catch {
      return raw;
    }
  }
  return raw;
};

const isPrimitive = (value) => value === null || typeof value !== 'object';

// A nested value inside an object row: primitives as they are, anything deeper on one line.
const inlineValue = (value) =>
  isPrimitive(value) ? String(value) : JSON.stringify(value);

// The "+N more" / "show less" switch under a list cut short.
const ExpandToggle = function ExpandToggle({ expanded, hidden, onToggle }) {
  const intl = useIntl();
  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'left' }}
    >
      {expanded
        ? intl.messages.SHOW_LESS
        : hidden > 0
          ? intl.formatMessage({ id: 'SHOW_MORE' }, { total: hidden })
          : intl.messages.SHOW_ALL}
    </Link>
  );
};

const SettingValue = function SettingValue({ value }) {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const parsed = parseValue(value);
  const toggle = () => setExpanded((prev) => !prev);
  // JSON carries its copy button inside its code block; any other value, beside it.
  const withCopy = (node) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{node}</Box>
      <CopyButton text={value} />
    </Box>
  );

  if (value === null || value === undefined || value === '') {
    return (
      <Typography variant="body1" color="text.disabled">
        —
      </Typography>
    );
  }

  if (typeof parsed === 'boolean') {
    return withCopy(
      <Chip
        size="small"
        label={parsed ? intl.messages.YES : intl.messages.NO}
        color={parsed ? 'success' : 'default'}
        variant="outlined"
        sx={{ fontWeight: 600 }}
      />
    );
  }

  if (typeof parsed === 'number') {
    return withCopy(<Typography sx={monoSx}>{parsed}</Typography>);
  }

  if (typeof parsed === 'string') {
    return withCopy(
      <Tooltip title={parsed}>
        <Typography variant="body1" sx={clampSx(3)}>
          {parsed}
        </Typography>
      </Tooltip>
    );
  }

  if (Array.isArray(parsed) && parsed.every(isPrimitive)) {
    const hidden = parsed.length - MAX_CHIPS;
    return (
      <CodeBlock
        copyText={value}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {(expanded ? parsed : parsed.slice(0, MAX_CHIPS)).map(
          (item, index) => (
            <Chip key={`${item}-${index}`} size="small" label={String(item)} />
          )
        )}
        {hidden > 0 && (
          <ExpandToggle expanded={expanded} hidden={hidden} onToggle={toggle} />
        )}
      </CodeBlock>
    );
  }

  // Objects, and arrays of objects (keyed by position). Opened, nested values are laid out in full.
  const entries = Object.entries(parsed);
  const hidden = entries.length - MAX_ROWS;
  const canExpand = hidden > 0 || entries.some(([, item]) => !isPrimitive(item));
  return (
    <CodeBlock
      copyText={value}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        columnGap: 1,
        rowGap: 0.25,
      }}
    >
      {(expanded ? entries : entries.slice(0, MAX_ROWS)).map(([key, item]) => (
        <React.Fragment key={key}>
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {key}
          </Box>
          <Box
            component="span"
            sx={
              expanded
                ? { overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }
                : clampSx(1)
            }
          >
            {expanded && !isPrimitive(item)
              ? JSON.stringify(item, null, 2)
              : inlineValue(item)}
          </Box>
        </React.Fragment>
      ))}
      {canExpand && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <ExpandToggle expanded={expanded} hidden={hidden} onToggle={toggle} />
        </Box>
      )}
    </CodeBlock>
  );
};

export default SettingValue;
